import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PushNotificationClient } from '../../../infrastructure/PushNotificationClient';
import type {
  SendPushNotificationToAllRequest,
  SendPushNotificationToAllResponse,
  SendPushNotificationRequest,
  SendPushNotificationResponse,
} from '../../../infrastructure/PushNotificationClient';

export type SendMode = 'all' | 'user';

export interface PushNotificationForm {
  title: string;
  body: string;
  url: string;
  sendMode: SendMode;
  receiverType: 'UserId' | 'Session';
  receiverIdentifier: string;
}

export const usePushNotification = () => {
  const client = new PushNotificationClient();
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<SendPushNotificationToAllResponse | SendPushNotificationResponse | null>(
    null,
  );

  const sendToAllMutation = useMutation({
    mutationFn: async (request: SendPushNotificationToAllRequest) => await client.sendToAll(request),
    onSuccess: (data) => {
      setLastResult(data);
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to send push notification to all');
    },
  });

  const sendToUserMutation = useMutation({
    mutationFn: async (request: SendPushNotificationRequest) => await client.sendToUser(request),
    onSuccess: (data) => {
      setLastResult(data);
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to send push notification to user');
    },
  });

  const sendNotification = useCallback(
    async (form: PushNotificationForm) => {
      const notificationData = {
        action: 'navigation' as const,
        payload: { url: form.url },
      };

      if (form.sendMode === 'all') {
        return sendToAllMutation.mutateAsync({
          title: form.title,
          body: form.body,
          data: notificationData,
        });
      } else {
        return sendToUserMutation.mutateAsync({
          receiver: {
            type: form.receiverType,
            identifier: form.receiverIdentifier,
          },
          title: form.title,
          body: form.body,
          data: notificationData,
        });
      }
    },
    [sendToAllMutation, sendToUserMutation],
  );

  const clearError = useCallback(() => setError(null), []);
  const clearResult = useCallback(() => setLastResult(null), []);

  return {
    sendNotification,
    isSending: sendToAllMutation.isPending || sendToUserMutation.isPending,
    error,
    lastResult,
    clearError,
    clearResult,
  };
};
